import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getOrders, updateOrder } from '../api';
import { hotelsById } from '../hotels';

const HOTELS = hotelsById();

function getItems(order) {
  return (order.items || order.cart || []).map((it) => ({
    name: it.name || it.title || 'Item',
    qty: it.qty || it.quantity || 1,
  }));
}

function timeAgo(ts) {
  if (!ts) return '';
  const m = Math.max(0, Math.floor((Date.now() - new Date(ts).getTime()) / 60000));
  if (m < 1) return 'just now';
  if (m === 1) return '1 min ago';
  return m + ' mins ago';
}

export default function DeliveryPanel() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(null);
  const [now, setNow] = useState(Date.now());
  const prevReadyIds = useRef(new Set());
  const isFirstLoad = useRef(true);
  const audioCtx = useRef(null);

  // Tick every 30s for time labels
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);

  const playAlert = useCallback(() => {
    try {
      if (!audioCtx.current) audioCtx.current = new (window.AudioContext || window.webkitAudioContext)();
      const ctx = audioCtx.current;
      // Three sharp rising blasts
      [0, 0.35, 0.70].forEach((delay) => {
        [220, 440, 880].forEach((freq, i) => {
          const o = ctx.createOscillator();
          const g = ctx.createGain();
          const comp = ctx.createDynamicsCompressor();
          o.type = 'sawtooth';
          o.frequency.setValueAtTime(freq, ctx.currentTime + delay);
          o.frequency.linearRampToValueAtTime(freq * 1.5, ctx.currentTime + delay + 0.18);
          comp.threshold.value = -6;
          comp.ratio.value = 12;
          o.connect(g); g.connect(comp); comp.connect(ctx.destination);
          const t0 = ctx.currentTime + delay + i * 0.02;
          g.gain.value = 0.0001;
          g.gain.exponentialRampToValueAtTime(0.9, t0 + 0.01);
          g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.22);
          o.start(t0); o.stop(t0 + 0.25);
        });
      });
    } catch (e) { /* ignore */ }
  }, []);

  const fetchOrders = useCallback(async () => {
    try {
      const data = await getOrders();
      const list = Array.isArray(data) ? data : (data?.orders || []);
      // Show all active delivery orders
      const deliveries = list.filter((o) => {
        const type = (o.orderType || o.type || '').toLowerCase();
        const st = (o.status || 'new').toLowerCase();
        return type.includes('deliver') && !['completed', 'delivered', 'rejected', 'refunded'].includes(st);
      });
      // Alert on newly-ready deliveries
      const readyIds = deliveries.filter((o) => o.status === 'ready').map((o) => o.orderId || o.id);
      const newReady = !isFirstLoad.current && readyIds.some((id) => !prevReadyIds.current.has(id));
      if (newReady) playAlert();
      isFirstLoad.current = false;
      prevReadyIds.current = new Set(readyIds);
      setOrders(deliveries);
    } catch (e) { /* keep last state */ }
    finally { setLoading(false); }
  }, [playAlert]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);
  useEffect(() => {
    const t = setInterval(fetchOrders, 10000);
    return () => clearInterval(t);
  }, [fetchOrders]);

  async function markDelivered(order) {
    const id = order.orderId || order.id;
    setCompleting(id);
    setOrders((prev) => prev.filter((o) => (o.orderId || o.id) !== id));
    try { await updateOrder(id, 'delivered'); } catch (e) { console.error(e); }
    setCompleting(null);
  }

  const pending = orders.filter((o) => o.status !== 'ready');
  const ready = orders.filter((o) => o.status === 'ready');

  return (
    <div style={s.page}>
      <header style={s.header}>
        <div style={s.logo}>
          <span style={{ ...s.dot, background: ready.length > 0 ? '#4ade80' : '#555', boxShadow: ready.length > 0 ? '0 0 10px #4ade80' : 'none' }} />
          Driver Board
        </div>
        <button style={s.refreshBtn} onClick={fetchOrders}>↺ Refresh</button>
      </header>

      {loading ? (
        <div style={s.empty}>Loading orders…</div>
      ) : orders.length === 0 ? (
        <div style={s.empty}>No active deliveries</div>
      ) : (
        <div style={s.body}>
          {ready.length > 0 && (
            <section>
              <div style={s.sectionLabel}>Ready for pickup</div>
              {ready.map((o) => (
                <OrderCard key={o.orderId || o.id} order={o} now={now}
                  onDeliver={markDelivered} delivering={completing === (o.orderId || o.id)} status="ready" />
              ))}
            </section>
          )}
          {pending.length > 0 && (
            <section>
              <div style={{ ...s.sectionLabel, color: '#888' }}>In kitchen ({pending.length})</div>
              {pending.map((o) => (
                <OrderCard key={o.orderId || o.id} order={o} now={now}
                  onDeliver={null} delivering={false} status={o.status || 'new'} />
              ))}
            </section>
          )}
        </div>
      )}
    </div>
  );
}

function OrderCard({ order, now, onDeliver, delivering, status }) {
  const items = getItems(order);
  const id = (order.orderId || order.id || '').toString().slice(-5).toUpperCase();
  const hotel = HOTELS[order.hotelId] || order.hotelId || 'Hotel';
  const isReady = status === 'ready';

  return (
    <div style={{ ...s.card, borderColor: isReady ? '#4ade80' : '#222' }}>
      <div style={s.cardTop}>
        <div>
          <span style={s.orderId}>#{id}</span>
          <span style={{ ...s.statusBadge, background: isReady ? 'rgba(74,222,128,0.15)' : 'rgba(255,255,255,0.06)', color: isReady ? '#4ade80' : '#888' }}>
            {isReady ? '✓ Ready' : status === 'preparing' ? 'Cooking…' : 'Incoming'}
          </span>
        </div>
        <span style={s.timeAgo}>{timeAgo(order.createdAt)}</span>
      </div>

      <div style={s.roomRow}>
        <span style={s.room}>Room {order.roomNumber || '—'}</span>
        <span style={s.hotelName}>{hotel}</span>
      </div>

      <div style={s.itemList}>
        {items.map((it, i) => (
          <div key={i} style={s.itemRow}>
            <span style={s.itemQty}>{it.qty}×</span>
            <span style={s.itemName}>{it.name}</span>
          </div>
        ))}
      </div>

      {order.notes && <div style={s.notes}>Note: {order.notes}</div>}

      {isReady && (
        <button
          style={{ ...s.deliverBtn, opacity: delivering ? 0.6 : 1 }}
          disabled={delivering}
          onClick={() => onDeliver(order)}
        >
          {delivering ? 'Marking…' : 'Mark Delivered'}
        </button>
      )}
    </div>
  );
}

const s = {
  page: { minHeight: '100vh', background: '#080808', color: '#fff', fontFamily: '-apple-system,BlinkMacSystemFont,"SF Pro Display",sans-serif', WebkitFontSmoothing: 'antialiased' },
  header: { position: 'sticky', top: 0, background: 'rgba(8,8,8,0.92)', backdropFilter: 'blur(16px)', borderBottom: '1px solid #1a1a1a', padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', zIndex: 10 },
  logo: { display: 'flex', alignItems: 'center', gap: 9, fontSize: 18, fontWeight: 700, letterSpacing: '-0.3px' },
  dot: { width: 9, height: 9, borderRadius: '50%', flexShrink: 0, transition: 'all 0.4s' },
  refreshBtn: { background: 'rgba(255,255,255,0.06)', border: '1px solid #222', borderRadius: 10, padding: '7px 14px', color: '#888', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  body: { padding: '16px', display: 'flex', flexDirection: 'column', gap: 24, maxWidth: 600, margin: '0 auto' },
  sectionLabel: { fontSize: 11, fontWeight: 800, letterSpacing: '1.2px', textTransform: 'uppercase', color: '#4ade80', marginBottom: 10 },
  empty: { padding: 80, textAlign: 'center', color: '#555', fontSize: 15 },
  card: { background: '#111', border: '1px solid #222', borderRadius: 18, padding: 18, marginBottom: 12, transition: 'border-color 0.3s' },
  cardTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 },
  orderId: { fontSize: 15, fontWeight: 800, marginRight: 8 },
  statusBadge: { fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 8 },
  timeAgo: { fontSize: 12, color: '#555' },
  roomRow: { display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 12 },
  room: { fontSize: 28, fontWeight: 800, letterSpacing: '-0.5px' },
  hotelName: { fontSize: 13, color: '#666' },
  itemList: { display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 12 },
  itemRow: { display: 'flex', gap: 8 },
  itemQty: { fontSize: 14, fontWeight: 800, minWidth: 28 },
  itemName: { fontSize: 14, fontWeight: 500, color: '#ccc' },
  notes: { fontSize: 12, color: '#fb923c', background: 'rgba(251,146,60,0.1)', border: '1px solid rgba(251,146,60,0.2)', borderRadius: 8, padding: '6px 10px', marginBottom: 12 },
  deliverBtn: { width: '100%', padding: '13px', borderRadius: 13, background: '#4ade80', color: '#000', fontSize: 15, fontWeight: 800, border: 'none', cursor: 'pointer', fontFamily: 'inherit' },
};
